import { getCurrencyCodes } from './get-currency-codes'
import { getFormatFromCurrencyCode } from './get-format-from-currency-code'

export interface Amount {
  amount: number
  currency: string
}

/**
 * Sanitize the ISO code
 *
 */
export const sanitizeCurrency = (currency: string) => {
  const currencyCode = currency?.toUpperCase()

  if (!getCurrencyCodes().includes(currencyCode)) {
    throw new Error(`Currency code ${currencyCode} is not supported.`)
  }

  return currencyCode
}

/**
 * Display the amount in the correct
 * currency format.
 */
export const displayCurrency = ({
  amount,
  currency: requestedCurrency,
}: Amount) => {
  if (!requestedCurrency) {
    return String(amount)
  }

  const sanitizedCurrency = sanitizeCurrency(requestedCurrency)

  return Intl.NumberFormat(getFormatFromCurrencyCode(sanitizedCurrency), {
    style: 'currency',
    currency: sanitizedCurrency,
  }).format(amount)
}

/**
 * calls displayCurrency along with the ISO code only
 * if the sanitized currency is not GBP
 *
 */
export const displayCurrencyWithISO = ({
  amount,
  currency: requestedCurrency,
}: Amount) => {
  const parts = [displayCurrency({ amount, currency: requestedCurrency })]
  if (requestedCurrency !== 'GBP') parts.unshift(requestedCurrency)
  return parts.join('')
}

/**
 * Returns the format in array parts as per Intl.NumberFormat
 *
 */
export const getCurrencyParts = ({
  amount,
  currency: requestedCurrency,
}: Amount) => {
  const sanitizedCurrency = sanitizeCurrency(requestedCurrency)

  return Intl.NumberFormat(getFormatFromCurrencyCode(sanitizedCurrency), {
    style: 'currency',
    currency: sanitizedCurrency,
  }).formatToParts(amount)
}

/**
 * Summarize price according to 100's, 1000's etc.
 *
 */
export const getAbbreviatedValue = ({ amount, currency }: Amount) => {
  const sanitizedCurrency = sanitizeCurrency(currency)
  const currencySymbol = getCurrencySymbol(sanitizedCurrency)

  const significantValues = Intl.NumberFormat(
    getFormatFromCurrencyCode(sanitizedCurrency),
    {
      notation: 'compact',
      // if under 100k we only need 1 significant digit (i.e. 1k ) with no digits
      minimumSignificantDigits: amount >= 100000 ? 2 : 1,
      maximumSignificantDigits: 3,
    },
  )
    .format(amount)
    .toLowerCase()

  return `${currencySymbol}${significantValues}`
}

/**
 * Return the symbiol for the currency
 *
 */
export const getCurrencySymbol = (currency: string) =>
  getCurrencyParts({
    amount: 0,
    currency,
  }).find(({ type }) => type === 'currency')?.value

/**
 * Generate a screen reader friendly version of a currency amount
 * that will be read as a complete monetary value instead of separate numbers
 */
export const getAccessibleCurrencyLabel = ({
  amount,
  currency: requestedCurrency,
}: Amount) => {
  if (!requestedCurrency) {
    return String(amount)
  }

  const sanitizedCurrency = sanitizeCurrency(requestedCurrency)

  const formatter = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: sanitizedCurrency,
    currencyDisplay: 'name',
  })

  return formatter.format(amount)
}
